import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';

export type Theme = 'light' | 'dark' | 'system';

const THEME_STORAGE_KEY = 'savant.portal.theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly preferenceState = signal<Theme>(readStoredTheme());
  private readonly systemPreference = signal<'light' | 'dark'>(systemTheme());

  readonly currentTheme = computed<'light' | 'dark'>(() => {
    const preference = this.preferenceState();
    return preference === 'system' ? this.systemPreference() : preference;
  });
  readonly preference = this.preferenceState.asReadonly();

  constructor() {
    effect(() => applyTheme(this.currentTheme()));

    if (isBrowser() && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const updateSystemPreference = (event: MediaQueryListEvent) => {
        this.systemPreference.set(event.matches ? 'dark' : 'light');
      };
      mediaQuery.addEventListener('change', updateSystemPreference);
      this.destroyRef.onDestroy(() => mediaQuery.removeEventListener('change', updateSystemPreference));
    }
  }

  setTheme(theme: Theme): void {
    this.preferenceState.set(theme);
    writeStoredTheme(theme);
  }

  getTheme(): Theme {
    return this.preferenceState();
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function systemTheme(): 'light' | 'dark' {
  if (!isBrowser() || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: 'light' | 'dark'): void {
  if (!isBrowser()) return;
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
}

function readStoredTheme(): Theme {
  if (!isBrowser()) return 'system';
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {}
  return 'system';
}

function writeStoredTheme(theme: Theme): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {}
}
